/**
 * Audience Segmentation Parliament — D3 hemicycle visualization for audience appeal.
 * Part of the Greenlight Engine for 1895.ai.
 */
import { useMemo, useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Globe, DollarSign, Heart, Film } from 'lucide-react';
import * as d3 from 'd3';

/* ─── constants ──────────────────────────────────────────────────── */

const TOTAL_SEATS = 100;
const SEAT_RADIUS = 6;
const ROW_RADII = [120, 150, 180, 210, 240];
const STAGGER_MS = 30;

/* ─── age colors (spec) ──────────────────────────────────────────── */

const AGE_COLORS = {
  '13_17': '#7B68EE',
  '18_24': '#FF6B35',
  '25_34': '#FFD700',
  '35_44': '#22C55E',
  '45_54': '#4A6FA5',
  '55_64': '#DEB887',
  '55_plus': '#DEB887',
  '65_plus': '#E6E6FA',
};

const AGE_LABELS = {
  '13_17': '13-17',
  '18_24': '18-24',
  '25_34': '25-34',
  '35_44': '35-44',
  '45_54': '45-54',
  '55_64': '55-64',
  '55_plus': '55+',
  '65_plus': '65+',
};

const GEOGRAPHY_COLORS = {
  north_america: '#34D399',
  europe: '#60A5FA',
  latin_america: '#4ADE80',
  middle_east_north_africa: '#FBBF24',
  sub_saharan_africa: '#F59E0B',
  south_asia: '#FB923C',
  east_asia: '#F87171',
  southeast_asia: '#FB7185',
  oceania: '#2DD4BF',
};

const GEOGRAPHY_LABELS = {
  north_america: 'N. America',
  europe: 'Europe',
  latin_america: 'Latin America',
  middle_east_north_africa: 'MENA',
  sub_saharan_africa: 'Sub-Saharan',
  south_asia: 'S. Asia',
  east_asia: 'E. Asia',
  southeast_asia: 'SE Asia',
  oceania: 'Oceania',
};

const GEOGRAPHY_FLAGS = {
  north_america: '🇺🇸',
  europe: '🇪🇺',
  latin_america: '🌎',
  middle_east_north_africa: '🌍',
  sub_saharan_africa: '🌍',
  south_asia: '🇮🇳',
  east_asia: '🇨🇳',
  southeast_asia: '🌏',
  oceania: '🇦🇺',
};

const INCOME_COLORS = {
  budget_conscious: '#86EFAC',
  middle_income: '#4ADE80',
  affluent: '#22C55E',
  ultra_premium: '#15803D',
};

const INCOME_LABELS = {
  budget_conscious: 'Budget',
  middle_income: 'Middle',
  affluent: 'Affluent',
  ultra_premium: 'Premium',
};

const SOCIAL_LABELS = {
  solo: 'Solo',
  date_night: 'Date Night',
  friends: 'Friends',
  family_kids: 'Family (Kids)',
  family_adults: 'Family (Adults)',
};

const SOCIAL_ICONS = {
  solo: '\uD83C\uDFAC',
  date_night: '\uD83D\uDC91',
  friends: '\uD83D\uDC65',
  family_kids: '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67',
  family_adults: '\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67',
};

/* ─── helpers ────────────────────────────────────────────────────── */

function scoreBgColor(score) {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  if (score >= 40) return '#F97316';
  return '#EF4444';
}

/* ─── D3 Hemicycle Parliament Chart ──────────────────────────────── */

function HemicycleChart({ segments, overallScore }) {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const [expandedSegment, setExpandedSegment] = useState(null);

  // Compute seat allocation
  const seatAllocation = useMemo(() => {
    const totalScore = segments.reduce((s, seg) => s + seg.score, 0);
    if (totalScore === 0) return [];

    let remaining = TOTAL_SEATS;
    const alloc = segments.map((seg, i) => {
      const share = Math.round((seg.score / totalScore) * TOTAL_SEATS);
      const seats = i === segments.length - 1 ? remaining : Math.min(share, remaining);
      remaining -= seats;
      return { ...seg, seatCount: seats };
    });
    return alloc;
  }, [segments]);

  // Compute seat positions using D3-style math
  const seatPositions = useMemo(() => {
    const seats = [];
    const cx = 260;
    const cy = 250;

    // Calculate seats per row proportional to circumference
    const totalCirc = ROW_RADII.reduce((sum, r) => sum + Math.PI * r, 0);
    const seatsPerRow = ROW_RADII.map((r) => {
      const raw = Math.round((Math.PI * r / totalCirc) * TOTAL_SEATS);
      return raw;
    });

    // Adjust to exactly TOTAL_SEATS
    let diff = TOTAL_SEATS - seatsPerRow.reduce((s, n) => s + n, 0);
    let idx = seatsPerRow.length - 1;
    while (diff !== 0) {
      seatsPerRow[idx] += diff > 0 ? 1 : -1;
      diff += diff > 0 ? -1 : 1;
      idx = (idx - 1 + seatsPerRow.length) % seatsPerRow.length;
    }

    // Build flat seat-color array from allocation
    const seatColors = [];
    seatAllocation.forEach((seg) => {
      for (let i = 0; i < seg.seatCount; i++) {
        seatColors.push({
          color: seg.color,
          label: seg.label,
          score: seg.score,
          reasoning: seg.reasoning,
          segKey: seg.key,
        });
      }
    });

    let globalIdx = 0;

    // Fill from bottom row (innermost) to top row (outermost)
    for (let row = 0; row < ROW_RADII.length; row++) {
      const r = ROW_RADII[row];
      const count = seatsPerRow[row];

      for (let s = 0; s < count; s++) {
        if (globalIdx >= TOTAL_SEATS) break;
        // Spread from PI (left) to 0 (right) for hemicycle
        const angle = Math.PI - (s / Math.max(count - 1, 1)) * Math.PI;
        const x = cx + r * Math.cos(angle);
        const y = cy - r * Math.sin(angle);

        const seatInfo = seatColors[globalIdx] || { color: '#333', label: '', score: 0 };
        seats.push({
          x,
          y,
          r: SEAT_RADIUS,
          color: seatInfo.color,
          label: seatInfo.label,
          score: seatInfo.score,
          reasoning: seatInfo.reasoning,
          segKey: seatInfo.segKey,
          index: globalIdx,
          row,
        });
        globalIdx++;
      }
    }

    return seats;
  }, [seatAllocation]);

  // D3 rendering
  useEffect(() => {
    if (!svgRef.current || seatPositions.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg.append('g');

    // Draw seats
    g.selectAll('circle.seat')
      .data(seatPositions)
      .join('circle')
      .attr('class', 'seat')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', 0)
      .attr('fill', (d) => d.color)
      .attr('stroke', (d) => d.color)
      .attr('stroke-width', 0.5)
      .attr('opacity', 0.9)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 0 2px rgba(0,0,0,0.3))')
      .on('mouseenter', function (event, d) {
        d3.select(this)
          .transition()
          .duration(150)
          .attr('r', SEAT_RADIUS * 1.6)
          .attr('opacity', 1)
          .style('filter', `drop-shadow(0 0 6px ${d.color})`);

        // Show tooltip
        const tooltip = d3.select(tooltipRef.current);
        tooltip
          .style('opacity', 1)
          .style('left', `${event.offsetX + 12}px`)
          .style('top', `${event.offsetY - 40}px`)
          .html(
            `<div style="font-family:'DM Sans',sans-serif;font-size:12px;font-weight:600;color:${d.color}">${d.label}</div>` +
            `<div style="font-family:'JetBrains Mono',monospace;font-size:14px;font-weight:700;color:#fff">${d.score}</div>` +
            (d.reasoning ? `<div style="font-size:10px;color:#9CA3AF;margin-top:4px;max-width:180px;line-height:1.4">${d.reasoning}</div>` : '')
          );
      })
      .on('mouseleave', function () {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', SEAT_RADIUS)
          .attr('opacity', 0.9)
          .style('filter', 'drop-shadow(0 0 2px rgba(0,0,0,0.3))');

        d3.select(tooltipRef.current).style('opacity', 0);
      })
      .on('click', function (event, d) {
        setExpandedSegment((prev) => (prev === d.segKey ? null : d.segKey));
      })
      .transition()
      .duration(400)
      .delay((d) => d.index * STAGGER_MS)
      .ease(d3.easeCubicOut)
      .attr('r', SEAT_RADIUS);

    // Center overall score
    const cx = 260;
    const cy = 250;

    g.append('text')
      .attr('x', cx)
      .attr('y', cy - 8)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', '#FFFFFF')
      .attr('font-family', "'JetBrains Mono', monospace")
      .attr('font-size', '32px')
      .attr('font-weight', '700')
      .attr('opacity', 0)
      .text(overallScore)
      .transition()
      .duration(600)
      .delay(TOTAL_SEATS * STAGGER_MS * 0.5)
      .attr('opacity', 1);

    g.append('text')
      .attr('x', cx)
      .attr('y', cy + 18)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('fill', '#6B7280')
      .attr('font-family', "'DM Sans', sans-serif")
      .attr('font-size', '11px')
      .attr('opacity', 0)
      .text('APPEAL SCORE')
      .transition()
      .duration(600)
      .delay(TOTAL_SEATS * STAGGER_MS * 0.5)
      .attr('opacity', 1);

  }, [seatPositions, overallScore]);

  const expandedData = expandedSegment
    ? segments.find((s) => s.key === expandedSegment)
    : null;

  return (
    <div className="relative">
      <div className="relative" style={{ width: '100%' }}>
        <svg
          ref={svgRef}
          viewBox="0 0 520 270"
          className="w-full"
          style={{ overflow: 'visible' }}
        />
        {/* Tooltip */}
        <div
          ref={tooltipRef}
          className="absolute pointer-events-none z-50 px-3 py-2 rounded-lg"
          style={{
            opacity: 0,
            background: 'rgba(17,17,21,0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(8px)',
            transition: 'opacity 0.15s ease',
          }}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 justify-center">
        {seatAllocation.map((seg) => (
          <button
            key={seg.key || seg.label}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200"
            style={{
              background: expandedSegment === seg.key ? `${seg.color}20` : 'transparent',
              border: expandedSegment === seg.key ? `1px solid ${seg.color}40` : '1px solid transparent',
            }}
            onClick={() => setExpandedSegment((prev) => (prev === seg.key ? null : seg.key))}
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: seg.color }}
            />
            <span className="text-[10px] text-[#9CA3AF]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {seg.label}
            </span>
            <span className="text-[10px] font-semibold text-[#E5E7EB]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {seg.score}
            </span>
          </button>
        ))}
      </div>

      {/* Expanded detail */}
      <AnimatePresence>
        {expandedData && (
          <motion.div
            key={expandedData.key}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-3 overflow-hidden"
          >
            <div
              className="rounded-lg px-4 py-3"
              style={{
                background: `${expandedData.color}10`,
                border: `1px solid ${expandedData.color}30`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: expandedData.color }}
                />
                <span className="text-sm font-semibold" style={{ color: expandedData.color, fontFamily: "'DM Sans', sans-serif" }}>
                  {expandedData.label}
                </span>
                <span className="text-sm font-bold text-white ml-auto" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {expandedData.score}
                </span>
              </div>
              {expandedData.reasoning && (
                <p className="text-xs text-[#9CA3AF] leading-relaxed mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {expandedData.reasoning}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Gender Gauges ──────────────────────────────────────────────── */

function GenderGauges({ genderAppeal }) {
  const male = genderAppeal?.male ?? 0;
  const female = genderAppeal?.female ?? 0;
  const universalAppeal = male >= 80 && female >= 80;

  return (
    <motion.div
      className="rounded-xl border border-[#222228] p-5"
      style={{ background: '#09090B' }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart size={14} className="text-[#6B7280]" />
          <h4 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Gender Appeal
          </h4>
        </div>
        {universalAppeal && (
          <motion.span
            className="text-[10px] font-semibold px-2.5 py-1 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #FFD700, #F59E0B)',
              color: '#09090B',
              fontFamily: "'DM Sans', sans-serif",
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, duration: 0.3 }}
          >
            Universal Appeal
          </motion.span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Male gauge */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#9CA3AF]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Male
            </span>
            <span className="text-sm font-bold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {male}%
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: '#222228' }}>
            <motion.div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, #2DD4BF40, #2DD4BF)`,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${male}%` }}
              transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            />
          </div>
          {genderAppeal?.male_reasoning && (
            <p className="text-[10px] text-[#6B7280] mt-1.5 leading-relaxed">
              {genderAppeal.male_reasoning}
            </p>
          )}
        </div>

        {/* Female gauge */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-[#9CA3AF]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Female
            </span>
            <span className="text-sm font-bold text-white" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {female}%
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: '#222228' }}>
            <motion.div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, #F472B640, #F472B6)`,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${female}%` }}
              transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
            />
          </div>
          {genderAppeal?.female_reasoning && (
            <p className="text-[10px] text-[#6B7280] mt-1.5 leading-relaxed">
              {genderAppeal.female_reasoning}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Geographic Regions (horizontal bars) ───────────────────────── */

function GeographicBars({ geoData }) {
  const [hoveredKey, setHoveredKey] = useState(null);

  const sorted = useMemo(() => {
    if (!geoData) return [];
    return Object.entries(geoData)
      .filter(([key]) => !key.includes('reasoning'))
      .map(([key, val]) => {
        const score = typeof val === 'object' ? val.score : val;
        const reasoning = typeof val === 'object' ? val.reasoning : geoData[`${key}_reasoning`];
        return {
          key,
          label: GEOGRAPHY_LABELS[key] || key,
          flag: GEOGRAPHY_FLAGS[key] || '',
          score: score || 0,
          reasoning,
          color: GEOGRAPHY_COLORS[key] || '#60A5FA',
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [geoData]);

  if (sorted.length === 0) return null;

  return (
    <motion.div
      className="rounded-xl border border-[#222228] p-4"
      style={{ background: '#09090B' }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.35 }}
    >
      <div className="flex items-center gap-2 mb-4">
        <Globe size={14} className="text-[#6B7280]" />
        <h4 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Top Regions
        </h4>
      </div>
      <div className="space-y-3">
        {sorted.map((region, i) => (
          <div
            key={region.key}
            className="group"
            onMouseEnter={() => setHoveredKey(region.key)}
            onMouseLeave={() => setHoveredKey(null)}
          >
            <div className="flex items-center gap-3">
              <span className="text-sm w-6 text-center">{region.flag}</span>
              <span className="text-xs text-[#9CA3AF] w-24 shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {region.label}
              </span>
              <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: '#222228' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: region.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${region.score}%` }}
                  transition={{ duration: 0.8, delay: 0.1 * i, ease: 'easeOut' }}
                />
              </div>
              <span
                className="text-xs font-semibold text-white w-8 text-right"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {region.score}
              </span>
            </div>
            <AnimatePresence>
              {hoveredKey === region.key && region.reasoning && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-[10px] text-[#6B7280] leading-relaxed ml-9 mt-1"
                >
                  {region.reasoning}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── Viewing Context Pills ──────────────────────────────────────── */

function ViewingContext({ socialContext }) {
  const items = useMemo(() => {
    if (!socialContext) return [];
    return Object.entries(socialContext)
      .filter(([key]) => !key.includes('reasoning'))
      .map(([key, val]) => {
        const score = typeof val === 'object' ? val.score : val;
        return {
          key,
          label: SOCIAL_LABELS[key] || key,
          icon: SOCIAL_ICONS[key] || '\uD83C\uDFAC',
          score: score || 0,
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [socialContext]);

  if (items.length === 0) return null;

  const topKey = items[0]?.key;

  return (
    <motion.div
      className="rounded-xl border border-[#222228] p-4"
      style={{ background: '#09090B' }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.45 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Film size={14} className="text-[#6B7280]" />
        <h4 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Viewing Context
        </h4>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => {
          const isTop = item.key === topKey;
          return (
            <motion.div
              key={item.key}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
              style={{
                background: isTop
                  ? 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(245,158,11,0.15))'
                  : 'rgba(255,255,255,0.04)',
                border: isTop
                  ? '1px solid rgba(255,215,0,0.4)'
                  : '1px solid rgba(255,255,255,0.08)',
                fontFamily: "'DM Sans', sans-serif",
                color: isTop ? '#FFD700' : '#9CA3AF',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.05, duration: 0.3 }}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
              <span
                className="font-semibold"
                style={{ fontFamily: "'JetBrains Mono', monospace", color: isTop ? '#FFD700' : '#E5E7EB' }}
              >
                {item.score}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ─── Income Class Pills / Stacked Bar ───────────────────────────── */

function IncomeDisplay({ incomeData }) {
  const items = useMemo(() => {
    if (!incomeData) return [];
    return Object.entries(incomeData)
      .filter(([key]) => !key.includes('reasoning'))
      .map(([key, val]) => {
        const score = typeof val === 'object' ? val.score : val;
        return {
          key,
          label: INCOME_LABELS[key] || key,
          score: score || 0,
          color: INCOME_COLORS[key] || '#22C55E',
        };
      });
  }, [incomeData]);

  if (items.length === 0) return null;

  const total = items.reduce((s, i) => s + i.score, 0);

  return (
    <motion.div
      className="rounded-xl border border-[#222228] p-4"
      style={{ background: '#09090B' }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.55 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <DollarSign size={14} className="text-[#6B7280]" />
        <h4 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Income Class
        </h4>
      </div>

      {/* Stacked bar */}
      <div className="h-4 rounded-full overflow-hidden flex" style={{ background: '#222228' }}>
        {items.map((item, i) => {
          const pct = total > 0 ? (item.score / total) * 100 : 0;
          return (
            <motion.div
              key={item.key}
              className="h-full"
              style={{ backgroundColor: item.color }}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, delay: 0.1 * i, ease: 'easeOut' }}
            />
          );
        })}
      </div>

      {/* Labels */}
      <div className="flex flex-wrap gap-4 mt-3 justify-center">
        {items.map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-xs text-[#9CA3AF]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {item.label}
            </span>
            <span className="text-xs font-semibold text-[#E5E7EB]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {item.score}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */

export default function AudienceParliament({ data }) {
  const breadth = data?.audience_breadth || {};
  const segmentation = data?.audience_segmentation || {};

  const overallScore = segmentation.overall_appeal_score ?? breadth.score ?? 0;

  /* Age segments for hemicycle */
  const ageSegments = useMemo(() => {
    if (!breadth.age_appeal) return [];
    return Object.entries(breadth.age_appeal)
      .filter(([key]) => !key.includes('reasoning'))
      .map(([key, val]) => {
        const score = typeof val === 'object' ? val.score : val;
        const reasoning = typeof val === 'object' ? val.reasoning : breadth.age_appeal[`${key}_reasoning`];
        return {
          key,
          label: AGE_LABELS[key] || key,
          score: score || 0,
          color: AGE_COLORS[key] || '#818CF8',
          reasoning,
        };
      });
  }, [breadth.age_appeal]);

  const ageOverall = useMemo(() => {
    if (ageSegments.length === 0) return 0;
    return Math.round(ageSegments.reduce((s, a) => s + a.score, 0) / ageSegments.length);
  }, [ageSegments]);

  if (!data?.audience_breadth) return null;

  return (
    <div className="space-y-5" style={{ background: '#09090B' }}>
      {/* ── Header ── */}
      <motion.div
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[#6B7280]" />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Audience Segmentation
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Overall Appeal</span>
          <span
            className="text-lg font-bold px-2 py-0.5 rounded-md"
            style={{
              color: scoreBgColor(overallScore),
              backgroundColor: `${scoreBgColor(overallScore)}15`,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {overallScore}
          </span>
        </div>
      </motion.div>

      {/* ── Age Hemicycle Parliament ── */}
      {ageSegments.length > 0 && (
        <motion.div
          className="rounded-xl border border-[#222228] p-4"
          style={{ background: '#09090B' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-[#6B7280]" />
              <h4 className="text-sm font-semibold text-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Age Appeal
              </h4>
            </div>
            <span
              className="text-lg font-bold"
              style={{ color: scoreBgColor(ageOverall), fontFamily: "'JetBrains Mono', monospace" }}
            >
              {ageOverall}
            </span>
          </div>
          <HemicycleChart segments={ageSegments} overallScore={ageOverall} />
        </motion.div>
      )}

      {/* ── Gender Gauges ── */}
      {breadth.gender_appeal && (
        <GenderGauges genderAppeal={breadth.gender_appeal} />
      )}

      {/* ── Geographic Regions ── */}
      {segmentation.geography_appeal && (
        <GeographicBars geoData={segmentation.geography_appeal} />
      )}

      {/* ── Viewing Context ── */}
      {breadth.social_context && (
        <ViewingContext socialContext={breadth.social_context} />
      )}

      {/* ── Income Class ── */}
      {(segmentation.income_appeal || breadth.income_appeal) && (
        <IncomeDisplay incomeData={segmentation.income_appeal || breadth.income_appeal} />
      )}

      {/* ── Primary / Secondary Audience ── */}
      {(segmentation.primary_audience || segmentation.secondary_audience) && (
        <motion.div
          className="rounded-xl border border-[#222228] p-5 space-y-4"
          style={{ background: '#09090B' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
        >
          {segmentation.primary_audience && (
            <div>
              <span className="text-xs uppercase tracking-wider text-[#6B7280] font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Primary Audience
              </span>
              <p className="text-[15px] text-white mt-1 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {segmentation.primary_audience}
              </p>
            </div>
          )}
          {segmentation.secondary_audience && (
            <div>
              <span className="text-xs uppercase tracking-wider text-[#6B7280] font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Secondary Audience
              </span>
              <p className="text-[15px] text-[#9CA3AF] mt-1 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {segmentation.secondary_audience}
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Marketing Insights & Distribution ── */}
      {(segmentation.marketing_insights || segmentation.distribution_recommendation) && (
        <motion.div
          className="rounded-xl border border-[#222228] p-5 space-y-4"
          style={{ background: '#09090B' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
        >
          {segmentation.marketing_insights && (
            <div>
              <span className="text-xs uppercase tracking-wider text-[#6B7280] font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Marketing Insights
              </span>
              <p className="text-sm text-[#9CA3AF] leading-relaxed mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {segmentation.marketing_insights}
              </p>
            </div>
          )}
          {segmentation.distribution_recommendation && (
            <div>
              <span className="text-xs uppercase tracking-wider text-[#6B7280] font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Distribution Recommendation
              </span>
              <p className="text-sm text-[#9CA3AF] leading-relaxed mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {segmentation.distribution_recommendation}
              </p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
